import { DatePipe, JsonPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { forkJoin, of, Subscription, timer } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';

import { ConfirmationModalComponent } from '../../components/modals/confirmation-modal/confirmation-modal.component';
import {
  JOB_STATUSES,
  JobAction,
  JobDetail,
  JobNormalizedStatus,
  JobSortBy,
  JobSortDir,
  QueueJob,
  QueuesOverviewResponse,
  QueueSummary,
} from '@core/api/status/types';
import {
  LIST_PAGINATOR_ALL,
  ListPaginatorComponent,
} from '@shared/components/list-paginator/list-paginator.component';
import { CommonService } from '@shared/services/common.service';
import { QueueAdminService } from '@shared/services/queue-admin.service';

const POLL_MS = 4000;
const PAGE_SIZE = 50;
const PAGE_SIZES = [25, 50, 100];
/** High enough to fetch every matching job when “All” is selected. */
const ALL_JOBS_LIMIT = 100_000;
const DEFAULT_SORT_BY: JobSortBy = 'createdAt';
const DEFAULT_SORT_DIR: JobSortDir = 'desc';

@Component({
  selector: 'app-queue-admin',
  templateUrl: './queue-admin.component.html',
  styleUrls: ['./queue-admin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    DatePipe,
    JsonPipe,
    MatButton,
    MatIconButton,
    MatCheckbox,
    MatFormField,
    MatIcon,
    MatInput,
    MatSelect,
    MatOption,
    MatProgressBar,
    MatProgressSpinner,
    MatTooltip,
    ListPaginatorComponent,
    MatTable,
    MatSort,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
  ],
})
export class QueueAdminComponent implements OnInit, OnDestroy {
  private readonly queueAdmin = inject(QueueAdminService);
  private readonly common = inject(CommonService);
  private readonly dialog = inject(MatDialog);

  private readonly subs: Subscription[] = [];
  private pollSub?: Subscription;

  protected readonly statuses = JOB_STATUSES;
  protected readonly pageSizes = PAGE_SIZES;
  protected readonly queueColumns: string[] = [
    'group',
    'name',
    'active',
    'waiting',
    'delayed',
    'failed',
    'completed',
    'paused',
  ];

  protected readonly jobColumns: string[] = [
    'select',
    'id',
    'name',
    'queue',
    'group',
    'status',
    'progress',
    'attempts',
    'createdAt',
    'actions',
  ];

  protected readonly overview = signal<QueuesOverviewResponse | null>(null);
  protected readonly overviewLoading = signal(false);
  protected readonly jobs = signal<QueueJob[]>([]);
  protected readonly jobsLoading = signal(false);
  protected readonly actionBusy = signal(false);
  protected readonly total = signal(0);
  protected readonly offset = signal(0);
  protected readonly limit = signal(PAGE_SIZE);
  protected readonly hasMore = signal(false);
  protected readonly sortBy = signal<JobSortBy>(DEFAULT_SORT_BY);
  protected readonly sortDir = signal<JobSortDir>(DEFAULT_SORT_DIR);

  protected readonly filterGroup = signal<string>('');
  protected readonly filterQueue = signal<string>('');
  protected readonly filterStatus = signal<JobNormalizedStatus | ''>('');
  protected readonly filterName = signal<string>('');

  protected readonly selectedJob = signal<QueueJob | null>(null);
  protected readonly jobDetail = signal<JobDetail | null>(null);
  protected readonly jobLogs = signal<string[]>([]);
  protected readonly detailLoading = signal(false);
  protected readonly selectedKeys = signal<ReadonlySet<string>>(new Set());

  protected readonly queueRows = computed(() => {
    return [...(this.overview()?.queues ?? [])].sort((a, b) => {
      const byGroup = a.group.localeCompare(b.group);
      return byGroup !== 0 ? byGroup : a.name.localeCompare(b.name);
    });
  });

  protected readonly groupOptions = computed(() => {
    const fromApi = (this.overview()?.groups ?? []).map(g => g.group);
    return Array.from(new Set(fromApi)).sort();
  });

  protected readonly queueOptions = computed(() => {
    const group = this.filterGroup();
    return (this.overview()?.queues ?? [])
      .filter(q => !group || q.group === group)
      .map(q => q.name)
      .sort();
  });

  protected readonly selectedCount = computed(() => this.selectedKeys().size);

  protected readonly pageIndex = computed(() => {
    const limit = this.limit();
    if (!limit || limit === LIST_PAGINATOR_ALL) {
      return 0;
    }
    return Math.floor(this.offset() / limit);
  });

  protected countOf(queue: QueueSummary, key: keyof NonNullable<QueueSummary['counts']>): number {
    return queue.counts?.[key] ?? 0;
  }

  private resolveRequestLimit(): number {
    const limit = this.limit();
    if (limit === LIST_PAGINATOR_ALL) {
      return ALL_JOBS_LIMIT;
    }
    return limit;
  }

  ngOnInit(): void {
    this.refreshAll();
    this.pollSub = timer(POLL_MS, POLL_MS).subscribe(() => {
      this.loadOverview(true);
    });
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.subs.forEach(s => s.unsubscribe());
  }

  protected refreshAll(): void {
    this.loadOverview();
    this.loadJobs();
  }

  protected loadOverview(silent = false): void {
    if (!silent) {
      this.overviewLoading.set(true);
    }
    const sub = this.queueAdmin
      .getQueues(this.filterGroup() || undefined)
      .pipe(
        catchError(err => {
          if (!silent) {
            this.common.openErrorSnackBar(err?.error?.error || 'Failed to load queues');
          }
          return of(null);
        }),
        finalize(() => this.overviewLoading.set(false))
      )
      .subscribe(res => {
        if (res) {
          this.overview.set(res);
        }
      });
    this.subs.push(sub);
  }

  protected loadJobs(): void {
    this.jobsLoading.set(true);
    const sub = this.queueAdmin
      .getJobs({
        group: this.filterGroup() || undefined,
        queue: this.filterQueue() || undefined,
        status: this.filterStatus() || undefined,
        name: this.filterName().trim() || undefined,
        limit: this.resolveRequestLimit(),
        offset: this.limit() === LIST_PAGINATOR_ALL ? 0 : this.offset(),
        sortBy: this.sortBy(),
        sortDir: this.sortDir(),
      })
      .pipe(
        catchError(err => {
          this.common.openErrorSnackBar(err?.error?.error || 'Failed to load jobs');
          return of(null);
        }),
        finalize(() => this.jobsLoading.set(false))
      )
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.jobs.set(res.jobs);
        this.total.set(res.total);
        this.hasMore.set(res.hasMore);
        this.pruneSelection(res.jobs);
      });
    this.subs.push(sub);
  }

  protected applyFilters(): void {
    this.offset.set(0);
    this.clearSelection();
    this.loadOverview();
    this.loadJobs();
  }

  protected clearFilters(): void {
    this.filterGroup.set('');
    this.filterQueue.set('');
    this.filterStatus.set('');
    this.filterName.set('');
    this.applyFilters();
  }

  protected onGroupFilterChange(group: string): void {
    this.filterGroup.set(group);
    if (this.filterQueue() && !this.queueOptions().includes(this.filterQueue())) {
      this.filterQueue.set('');
    }
  }

  protected selectQueue(queue: QueueSummary): void {
    this.filterGroup.set(queue.group);
    this.filterQueue.set(queue.name);
    this.offset.set(0);
    this.clearSelection();
    this.loadJobs();
  }

  protected handlePage(event: PageEvent): void {
    this.limit.set(event.pageSize);
    if (event.pageSize === LIST_PAGINATOR_ALL) {
      this.offset.set(0);
    } else {
      this.offset.set(event.pageIndex * event.pageSize);
    }
    this.loadJobs();
  }

  protected handleSort(sort: Sort): void {
    const active = sort.active as JobSortBy | '';
    if (!active || !sort.direction) {
      this.sortBy.set(DEFAULT_SORT_BY);
      this.sortDir.set(DEFAULT_SORT_DIR);
    } else {
      this.sortBy.set(active);
      this.sortDir.set(sort.direction);
    }
    this.offset.set(0);
    this.loadJobs();
  }

  protected jobKey(job: QueueJob): string {
    return `${job.queue}::${job.id}`;
  }

  protected isSelected(job: QueueJob): boolean {
    return this.selectedKeys().has(this.jobKey(job));
  }

  protected toggleSelect(job: QueueJob): void {
    const key = this.jobKey(job);
    const next = new Set(this.selectedKeys());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.selectedKeys.set(next);
  }

  protected toggleSelectAll(): void {
    const keys = this.jobs().map(j => this.jobKey(j));
    if (this.isAllSelected()) {
      this.selectedKeys.set(new Set());
      return;
    }
    this.selectedKeys.set(new Set(keys));
  }

  protected isAllSelected(): boolean {
    const keys = this.jobs().map(j => this.jobKey(j));
    const selected = this.selectedKeys();
    return keys.length > 0 && keys.every(k => selected.has(k));
  }

  protected clearSelection(): void {
    this.selectedKeys.set(new Set());
  }

  protected openDetail(job: QueueJob): void {
    this.selectedJob.set(job);
    this.detailLoading.set(true);
    this.jobDetail.set(null);
    this.jobLogs.set([]);

    const sub = forkJoin({
      detail: this.queueAdmin.getJob(job.id, job.queue).pipe(catchError(() => of(null))),
      logs: this.queueAdmin.getJobLogs(job.id, job.queue).pipe(catchError(() => of(null))),
    })
      .pipe(finalize(() => this.detailLoading.set(false)))
      .subscribe(({ detail, logs }) => {
        this.jobDetail.set(detail);
        this.jobLogs.set(logs?.logs ?? []);
      });
    this.subs.push(sub);
  }

  protected closeDetail(): void {
    this.selectedJob.set(null);
    this.jobDetail.set(null);
    this.jobLogs.set([]);
  }

  protected actionsFor(status: JobNormalizedStatus | string): JobAction[] {
    switch (status) {
      case 'failed':
        return ['retry', 'remove'];
      case 'delayed':
        return ['promote', 'remove'];
      case 'in progress':
        return ['cancel', 'force-remove'];
      case 'waiting':
      case 'completed':
        return ['remove'];
      default:
        return ['remove'];
    }
  }

  protected actionLabel(action: JobAction): string {
    switch (action) {
      case 'retry':
        return 'Retry';
      case 'promote':
        return 'Promote';
      case 'remove':
        return 'Remove';
      case 'cancel':
        return 'Cancel';
      case 'force-remove':
        return 'Force remove';
      case 'discard':
        return 'Discard';
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  }

  protected progressValue(progress: number | Record<string, unknown>): number {
    return typeof progress === 'number' ? progress : 0;
  }

  protected metricClass(key: string, value: number): string {
    if (value <= 0) {
      return 'metric';
    }
    if (key === 'failed') {
      return 'metric metric--danger';
    }
    if (key === 'active') {
      return 'metric metric--active';
    }
    if (key === 'waiting' || key === 'delayed') {
      return 'metric metric--warn';
    }
    if (key === 'completed') {
      return 'metric metric--ok';
    }
    return 'metric metric--hot';
  }

  protected runAction(job: QueueJob, action: JobAction, event?: Event): void {
    event?.stopPropagation();

    if (action === 'force-remove') {
      this.confirmAndRun(
        `Force-remove job ${job.id}? This can abandon in-flight work.`,
        'Force remove job',
        'Force remove',
        () => this.executeAction(job, action)
      );
      return;
    }

    this.executeAction(job, action);
  }

  protected runBulkAction(action: JobAction): void {
    const selected = Array.from(this.selectedKeys());
    if (!selected.length) {
      return;
    }

    const jobIds = selected.map(key => {
      const sep = key.indexOf('::');
      return { queue: key.slice(0, sep), id: key.slice(sep + 2) };
    });

    const run = () => {
      this.actionBusy.set(true);
      const sub = this.queueAdmin
        .runBulkJobAction({ action, jobIds })
        .pipe(
          catchError(err => {
            this.common.openErrorSnackBar(err?.error?.error || `Bulk ${action} failed`);
            return of(null);
          }),
          finalize(() => this.actionBusy.set(false))
        )
        .subscribe(res => {
          if (!res) {
            return;
          }
          if (res.failedCount > 0) {
            this.common.openWarningSnackBar(res.message);
          } else {
            this.common.openSuccessSnackBar(res.message);
          }
          this.clearSelection();
          this.refreshAll();
          const current = this.selectedJob();
          if (current && selected.includes(this.jobKey(current))) {
            this.closeDetail();
          }
        });
      this.subs.push(sub);
    };

    if (action === 'force-remove') {
      this.confirmAndRun(
        `Force-remove ${jobIds.length} selected job(s)? This can abandon in-flight work.`,
        'Force remove jobs',
        'Force remove',
        run
      );
      return;
    }

    this.confirmAndRun(
      `Apply "${this.actionLabel(action)}" to ${jobIds.length} selected job(s)?`,
      'Bulk action',
      this.actionLabel(action),
      run
    );
  }

  private executeAction(job: QueueJob, action: JobAction): void {
    this.actionBusy.set(true);
    const sub = this.queueAdmin
      .runJobAction(job.id, { action, queue: job.queue })
      .pipe(
        catchError(err => {
          const status = err?.status;
          const message = err?.error?.message || err?.error?.error || `Action '${action}' failed`;
          if (status === 409) {
            this.common.openWarningSnackBar(
              `${message}. Prefer Cancel, or Force remove if the job is hung.`
            );
          } else {
            this.common.openErrorSnackBar(message);
          }
          return of(null);
        }),
        finalize(() => this.actionBusy.set(false)),
        switchMap(res => {
          if (!res) {
            return of(null);
          }
          if (res.ok) {
            this.common.openSuccessSnackBar(res.message);
          } else {
            this.common.openWarningSnackBar(res.message + (res.reason ? `: ${res.reason}` : ''));
          }
          return of(res);
        })
      )
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.refreshAll();
        if (this.selectedJob()?.id === job.id && this.selectedJob()?.queue === job.queue) {
          if (action === 'remove' || action === 'force-remove') {
            this.closeDetail();
          } else {
            this.openDetail(job);
          }
        }
      });
    this.subs.push(sub);
  }

  private confirmAndRun(
    text: string,
    title: string,
    confirmLabel: string,
    onConfirm: () => void
  ): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: { text, title, confirmLabel },
    });
    const dialogSub = dialogRef.componentInstance.actionCallback.subscribe(() => {
      onConfirm();
      dialogRef.close();
      dialogSub.unsubscribe();
    });
  }

  private pruneSelection(jobs: QueueJob[]): void {
    const valid = new Set(jobs.map(j => this.jobKey(j)));
    const next = new Set(Array.from(this.selectedKeys()).filter(k => valid.has(k)));
    this.selectedKeys.set(next);
  }
}
